import { useEffect, useState } from "react";
import { Trophy, Check, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatErr } from "@/lib/api";
import { confetti } from "@/lib/celebrate";
import { POINTS_EVENT } from "@/components/PointsBurst";
import { useAuth } from "@/context/AuthContext";

export function DailyProgress() {
  const { refreshUser } = useAuth();
  const [p, setP] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get("/games/daily-progress").then(({ data }) => setP(data)).catch(() => {}); }, []);
  if (!p || !p.total) return null;
  const all = p.done >= p.total;
  const pct = Math.round((p.done / p.total) * 100);
  const claim = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/games/daily-progress/claim");
      setP(data); confetti({ count: 80 }); refreshUser?.();
      window.dispatchEvent(new CustomEvent(POINTS_EVENT, { detail: { amount: data.awarded, reason: "Tages-Fortschritt: alles geschafft! 🏆" } }));
      toast.success(`+${data.awarded} Bonuspunkte – alles geschafft! 🏆`);
    } catch (e) { toast.error(formatErr(e)); } finally { setBusy(false); }
  };
  return (
    <div className={`rounded-3xl p-4 sm:p-5 border ${all ? "bg-gradient-to-br from-amber-50 via-white to-rose-50 border-amber-200" : "bg-white border-pink-100"}`} data-testid="daily-progress">
      <div className="flex items-center gap-3">
        <span className={`w-11 h-11 rounded-2xl flex items-center justify-center shrink-0 ${all ? "bg-gradient-to-br from-amber-300 to-amber-500 text-white" : "pink-gradient text-white"}`}><Trophy className="w-5 h-5" /></span>
        <div className="flex-1 min-w-0">
          <p className="font-display text-lg font-extrabold leading-tight" data-testid="daily-progress-count">{p.done} von {p.total} erledigt</p>
          <p className="text-xs text-slate-500">{p.claimed ? "Tagesbonus abgeholt ✓ – morgen geht's weiter!" : all ? "Alles geschafft – hol dir deinen Bonus!" : `Schaff heute alle Spiele für +${p.bonus} Bonuspunkte`}</p>
        </div>
        {all && !p.claimed && (
          <button onClick={claim} disabled={busy} data-testid="daily-progress-claim" className="shrink-0 h-10 px-4 rounded-full bg-gradient-to-r from-amber-400 to-amber-600 text-white text-sm font-extrabold shadow-md shadow-amber-200 active:scale-95 transition-transform wheel-cta">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : `+${p.bonus} abholen`}
          </button>
        )}
      </div>
      <div className="mt-3 h-2.5 rounded-full bg-rose-50 overflow-hidden"><div className={`h-full rounded-full transition-[width] duration-700 ${all ? "bg-gradient-to-r from-amber-300 to-amber-500" : "pink-gradient"}`} style={{ width: `${Math.max(pct, 4)}%` }} data-testid="daily-progress-bar" /></div>
      <div className="mt-3 flex flex-wrap gap-1.5">
        {p.items.map((i) => (
          <span key={i.id} data-testid={`daily-progress-${i.id}`} className={`inline-flex items-center gap-1 px-2.5 h-7 rounded-full text-xs font-bold ${i.done ? "bg-emerald-50 text-emerald-700" : "bg-slate-100 text-slate-500"}`}>
            {i.done && <Check className="w-3.5 h-3.5" />}{i.label}
          </span>
        ))}
      </div>
    </div>
  );
}
